import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault
} from 'use-query-params';
import {
  Box,
  Button,
  ButtonGroup,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  TablePagination,
  LinearProgress
} from '@material-ui/core';
import {
  ViewList as ViewListIcon,
  ViewColumn as ViewColumnIcon
} from '@material-ui/icons';

import SearchBox from '../../common/SearchBox';
import { ApiParams } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import { formatDate } from '../../common/formatter';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import TaskBoard from '../tasks/TaskBoard';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import SupplierListFilter, {
  SupplierFilterOptions
} from './components/SupplierListFilter';
import SupplierCreateDialog from './dialogs/SupplierCreateDialog';
import { Supplier } from './Supplier.interface';
import { useFindSuppliers } from './Supplier.hooks';

const SupplierListPage: React.FC = () => {
  const [view, setView] = useQueryParam('view', StringParam);
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);
  const [apiParams, setApiParams] = useState<ApiParams>({});
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const history = useHistory();

  const { supplierList, isLoading, loadingError, findSuppliers } =
    useFindSuppliers();

  useEffect(() => {
    findSuppliers({ search, page, limit, order, direction, ...apiParams });
  }, [page, limit, order, direction, findSuppliers, search, apiParams]);

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Suppliers</h2>
        <Box ml={2}>
          <ButtonGroup variant='contained' color='primary'>
            <Button size='small' onClick={() => setView('List')}>
              <ViewListIcon />
            </Button>
            <Button size='small' onClick={() => setView('Board')}>
              <ViewColumnIcon />
            </Button>
          </ButtonGroup>
        </Box>
        {view !== 'Board' && (
          <>
            <SearchBox value={search} onSearch={setSearch}>
              <SupplierListFilter
                onFilter={(options: SupplierFilterOptions) => {
                  const { status } = options;
                  const params: ApiParams = { ...apiParams, status };
                  !status && delete params.status;
                  setApiParams(params);
                }}
              />
            </SearchBox>
            <PageTemplateHeaderActions>
              <Button
                variant='contained'
                color='primary'
                onClick={() => setIsOpenDialog(true)}
              >
                Add Supplier
              </Button>
            </PageTemplateHeaderActions>
          </>
        )}
      </PageTemplateHeader>
      {view === 'Board' ? (
        <PageTemplateDashboard>
          <TaskBoard
            entity={WorkflowEntity.Supplier}
            onSelectTask={task => {
              const taskId = task.parent?.id || task.id;
              history.push(`/portal/suppliers/task/${taskId}`);
            }}
          />
        </PageTemplateDashboard>
      ) : (
        <PageTemplateCardOver>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'id'}
                      direction={getSortDirection('id')}
                      onClick={() => handleSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={order === 'companyName'}
                      direction={getSortDirection('companyName')}
                      onClick={() => handleSort('companyName')}
                    >
                      Supplier
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'task.status'}
                      direction={getSortDirection('task.status')}
                      onClick={() => handleSort('task.status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'modified'}
                      direction={getSortDirection('modified')}
                      onClick={() => handleSort('modified')}
                    >
                      Last Modified
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'reviewDate'}
                      direction={getSortDirection('reviewDate')}
                      onClick={() => handleSort('reviewDate')}
                    >
                      Next Review Date
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={3} padding='none'>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {supplierList &&
                  supplierList.data.map((supplier: Supplier) => (
                    <TableRow
                      hover
                      key={supplier.id}
                      onClick={() =>
                        history.push(
                          `/portal/suppliers/task/${supplier.taskId}`
                        )
                      }
                    >
                      <TableCell>SUPPLIER-{supplier.id}</TableCell>
                      <TableCell>{supplier.companyName}</TableCell>
                      <TableCell>
                        <TaskStatusLabel task={supplier.task} />
                      </TableCell>
                      <TableCell>{formatDate(supplier.modified)}</TableCell>
                      <TableCell>
                        {supplier.reviewDate
                          ? formatDate(supplier.reviewDate, true)
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component='div'
            count={supplierList?.total || 0}
            rowsPerPage={limit}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={event => {
              setLimit(+event.target.value);
              setPage(0);
            }}
          />
        </PageTemplateCardOver>
      )}
      <AppSnackbar severity='error' open={Boolean(loadingError)}>
        There was an error loading suppliers.
      </AppSnackbar>
      {isOpenDialog && (
        <SupplierCreateDialog
          open={isOpenDialog}
          onCancel={() => setIsOpenDialog(false)}
          onCreated={() => {
            setIsOpenDialog(false);
            findSuppliers(); // refresh data
          }}
        />
      )}
    </PageTemplate>
  );
};

export default SupplierListPage;
